import React, { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowUpRight, Check, Copy, ShieldCheck, Ban } from 'lucide-react';
import { DataQueryResult } from '../../Types/DataServiceType';
import { copyToClipboard } from './clipboard';
import { buildRecordPath } from './recordLink';

interface Props {
  result: DataQueryResult | null;
  loading?: boolean;
  error?: string;
  emptyMessage?: string;
  onRowEdit?: (row: Record<string, unknown>) => void;
  onRowDelete?: (row: Record<string, unknown>) => void;
  canWrite?: boolean;
  /**
   * Makes rows openable, and enables the "primary key" column marker.
   *
   * <p>Both come from the table's own metadata rather than from the result,
   * because a query result is mapped straight off JDBC and carries no key
   * information of its own. `primaryKeyLabels` must be limited to the columns
   * the current result actually projects - a partial select that omits the key
   * has no addressable row, and the grid should say so by staying inert.</p>
   */
  recordLink?: {
    connectionId: string;
    table: string;
    primaryKeyLabels: string[];
  };
}

const MAX_PREVIEW = 500;

const renderCell = (value: unknown): string => {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/** Copy button that confirms in place, so no toast is needed. */
export const CopyButton = ({
  text,
  label = 'Copy',
  className = '',
}: {
  text: string;
  label?: string;
  className?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const handle = async () => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={!text}
      className={`flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest transition disabled:opacity-50 ${
        copied
          ? 'border-emerald-500/40 text-emerald-400'
          : 'text-cyan-400 hover:border-cyan-500/40'
      } ${className}`}
      title={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? 'Copied' : label}
    </button>
  );
};

const DataResultGrid = ({
  result,
  loading = false,
  error = '',
  emptyMessage = 'Run a query to see results here.',
  onRowEdit,
  onRowDelete,
  canWrite = false,
  recordLink,
}: Props) => {
  const columns = useMemo(() => result?.columns ?? [], [result]);

  const keyLabels = useMemo(() => recordLink?.primaryKeyLabels ?? [], [recordLink]);

  const isKeyColumn = useCallback(
    (label: string) => keyLabels.includes(label),
    [keyLabels]
  );

  /**
   * Address of a single row, or null when this result has no addressable row.
   *
   * <p>Fails closed on a null or blank key value: a record page opened without
   * a real key would silently resolve to the wrong row, or to none.</p>
   */
  const hrefForRow = useCallback(
    (row: Record<string, unknown>): string | null => {
      if (!recordLink || !recordLink.connectionId || !recordLink.table) return null;
      if (keyLabels.length === 0) return null;

      const primaryKey: Record<string, string> = {};
      for (const label of keyLabels) {
        const value = row[label];
        if (value === null || value === undefined || value === '') return null;
        primaryKey[label] = String(value);
      }

      return buildRecordPath({
        connectionId: recordLink.connectionId,
        table: recordLink.table,
        primaryKey,
      });
    },
    [recordLink, keyLabels]
  );

  if (loading) {
    return (
      <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
        {[0, 1, 2, 3].map((idx) => (
          <div key={idx} className="h-4 w-full animate-pulse rounded bg-slate-800/70" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center">
        <AlertTriangle size={22} className="mx-auto mb-2 text-rose-400" />
        <p className="text-sm font-bold text-rose-400">{error}</p>
      </div>
    );
  }

  if (!result || result.rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
        <p className="text-xs font-black uppercase tracking-widest text-slate-400">{emptyMessage}</p>
      </div>
    );
  }

  const visibleRows = result.rows.slice(0, MAX_PREVIEW);
  const hasActions = canWrite && (onRowEdit || onRowDelete);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3">
        <div className="flex flex-wrap items-center gap-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">
          <span>
            <span className="text-cyan-400">{result.rowCount}</span> row
            {result.rowCount === 1 ? '' : 's'}
          </span>
          <span>
            <span className="text-cyan-400">{result.durationMs}</span> ms
          </span>
          {result.connectionAlias && <span className="text-slate-500">{result.connectionAlias}</span>}
          {result.tableName && <span className="text-slate-500">{result.tableName}</span>}
        </div>

        {result.truncated && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-400">
            <Ban size={11} /> Result truncated at the row cap
          </span>
        )}

        <CopyButton text={result.executedSql} label="Copy SQL" />
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
        <div className="overflow-auto" style={{ maxHeight: '32rem' }}>
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-slate-800 bg-slate-900 text-[10px] font-black uppercase tracking-widest text-slate-500">
                {columns.map((column) => (
                  <th key={column.label} className="whitespace-nowrap px-4 py-3">
                    <span className="flex flex-col">
                      <span>{column.label}</span>
                      {isKeyColumn(column.label) && (
                        <span className="mt-0.5 text-[9px] text-cyan-500">primary key</span>
                      )}
                      {column.typeName && (
                        <span className="mt-0.5 text-[9px] font-semibold normal-case tracking-normal text-slate-600">
                          {column.typeName}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
                {hasActions && <th className="sticky right-0 px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, rowIndex) => {
                const asRecord: Record<string, unknown> = {};
                columns.forEach((column, i) => {
                  asRecord[column.label] = row[i];
                });

                const href = hrefForRow(asRecord);

                return (
                  <tr
                    key={rowIndex}
                    onClick={
                      href
                        ? () => window.open(href, '_blank', 'noopener,noreferrer')
                        : undefined
                    }
                    title={href ? 'Open this record in a new tab' : undefined}
                    className={`group border-b border-slate-800/60 transition last:border-0 ${
                      href
                        ? 'cursor-pointer hover:bg-cyan-500/[0.07]'
                        : 'hover:bg-slate-800/20'
                    }`}
                  >
                    {columns.map((column, cellIndex) => (
                      <td
                        key={`${rowIndex}-${column.label}`}
                        className={`max-w-[320px] truncate px-4 py-2.5 text-xs ${
                          row[cellIndex] === null || row[cellIndex] === undefined
                            ? 'text-slate-600'
                            : 'text-slate-300'
                        }`}
                        title={renderCell(row[cellIndex])}
                      >
                        {renderCell(row[cellIndex])}
                      </td>
                    ))}
                    {href && (
                      <td className="w-10 px-2 py-2.5 text-right">
                        <ArrowUpRight
                          size={14}
                          className="inline text-cyan-500/50 transition group-hover:text-cyan-400"
                        />
                      </td>
                    )}
                    {hasActions && (
                      <td className="sticky right-0 bg-slate-900/80 px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {onRowEdit && (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                onRowEdit(asRecord);
                              }}
                              className="rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                            >
                              Edit
                            </button>
                          )}
                          {onRowDelete && (
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                onRowDelete(asRecord);
                              }}
                              className="rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {result.rows.length > visibleRows.length && (
        <p className="text-center text-[11px] font-bold text-slate-500">
          Showing the first {visibleRows.length} of {result.rows.length} returned rows. Narrow the
          filters or raise the limit to see the rest.
        </p>
      )}


      <details className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-3">
        <summary className="cursor-pointer text-[11px] font-black uppercase tracking-widest text-slate-400">
          Executed statement
        </summary>
        <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-300">
          {result.executedSql}
        </pre>
        {result.boundParameters && Object.keys(result.boundParameters).length > 0 && (
          <>
            <p className="mt-3 flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-400">
              <ShieldCheck size={13} className="text-cyan-500" /> Bound values
            </p>
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-300">
              {JSON.stringify(result.boundParameters, null, 2)}
            </pre>
          </>
        )}
      </details>

    </motion.div>
  );
};

export default DataResultGrid;
